import type { OwnerParticipant, TimetableGroup } from "@teamna/shared";
import {
  Avatar,
  Badge,
  Button,
  DataTable,
  InlineError,
  Select,
  StatTile,
  showToast,
  type DataTableColumn,
} from "../../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useParticipants, usePlaceParticipant, useTimetable } from "@teamna/web-client/hooks/use-setup.ts";
import { describeWeekdays, SaveBar, SetupFrame } from "./frame.tsx";

/**
 * Setup step 7: put every participant in a group (mobile spec 5.5), as a table, not the card deck.
 *
 * One row per participant with a group select that saves on change, plus "Not sure yet" for the
 * ones the owner has to ask a staff member about. The member's own pick is shown next to it.
 * "Confirm all" takes the member's pick for every row that has one and is not placed yet. The step
 * is done when nobody is left unplaced (placed or "not sure" both count), which the API works out.
 */
export function SetupPlacementScreen() {
  return (
    <SetupFrame step="placement">{(businessId) => <PlacementBody businessId={businessId} />}</SetupFrame>
  );
}

const UNSURE = "unsure";

/** The group a row would take on "Confirm all": the member's pick. */
function pickFor(row: OwnerParticipant): string | null {
  return row.groupId ?? row.memberPickedGroupId;
}

function placementColumns({
  label,
  groupName,
  groups,
  place,
  choose,
}: {
  label: ReturnType<typeof useLabels>["label"];
  groupName: (id: string | null) => string | null;
  groups: TimetableGroup[];
  place: ReturnType<typeof usePlaceParticipant>;
  choose: (row: OwnerParticipant, value: string) => void;
}): DataTableColumn<OwnerParticipant>[] {
  return [
    {
      id: "name",
      header: label("participant", { capital: true }),
      sortValue: (row) => row.displayName,
      cell: (row) => (
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={row.displayName} photoUrl={row.photoUrl} size={32} />
          <div className="min-w-0">
            <p className="font-semibold text-ink">
              <span dir="auto">{row.displayName}</span>
              {row.age !== null ? <span className="font-normal text-ink-3">, {row.age}</span> : null}
            </p>
            <p className="truncate text-[13px] text-ink-3">
              {row.members.map((member) => member.name).join(" and ") || "No account yet"}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "days",
      header: "Usual days",
      cell: (row) => describeWeekdays(row.usualWeekdays),
      hideOnPhone: true,
    },
    {
      id: "pick",
      header: `${label("member", { capital: true })} picked`,
      hideOnPhone: true,
      cell: (row) =>
        row.memberPickedGroupId ? (
          (groupName(row.memberPickedGroupId) ?? `A removed ${label("group")}`)
        ) : (
          <span className="text-ink-3">Not sure</span>
        ),
    },
    {
      id: "group",
      header: label("group", { capital: true }),
      width: "220px",
      sortValue: (row) => row.groupName,
      cell: (row) => (
        <Select
          label={`${label("group", { capital: true })} for ${row.displayName}`}
          className="w-full"
          value={row.placementStatus === "unsure" ? UNSURE : (row.groupId ?? "")}
          disabled={place.isPending && place.variables?.id === row.id}
          onChange={(event) => choose(row, event.target.value)}
        >
          <option value="" disabled>
            Choose
          </option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name} ({group.participantCount})
            </option>
          ))}
          <option value={UNSURE}>Not sure yet</option>
        </Select>
      ),
    },
    {
      id: "status",
      header: "Status",
      sortValue: (row) => row.placementStatus,
      cell: (row) =>
        row.placementStatus === "placed" ? (
          <Badge tone="confirmed">Placed</Badge>
        ) : row.placementStatus === "unsure" ? (
          <Badge tone="late">Not sure yet</Badge>
        ) : (
          <Badge tone="quiet">To place</Badge>
        ),
    },
  ];
}

function PlacementBody({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const participants = useParticipants(businessId);
  const timetable = useTimetable(businessId);
  const place = usePlaceParticipant(businessId);

  const rows = participants.data ?? [];
  const groups = (timetable.data?.groups ?? []).filter((group) => group.kind === "group" && !group.openToAll);
  const groupName = (id: string | null) => groups.find((group) => group.id === id)?.name ?? null;

  const placed = rows.filter((row) => row.placementStatus === "placed").length;
  const unsure = rows.filter((row) => row.placementStatus === "unsure").length;
  const remaining = rows.filter((row) => row.placementStatus === "unplaced");
  const confirmable = remaining.filter((row) => pickFor(row) !== null);

  function choose(row: OwnerParticipant, value: string) {
    if (value === "") return;
    if (value === UNSURE) {
      place.mutate({ id: row.id, groupId: null, placementStatus: "unsure" });
    } else {
      place.mutate(
        { id: row.id, groupId: value, confirm: true },
        {
          onSuccess: () =>
            showToast(`${row.displayName} is in ${groupName(value) ?? `the ${label("group")}`}`),
        },
      );
    }
  }

  async function confirmAll() {
    const results = await Promise.allSettled(
      confirmable.map((row) => place.mutateAsync({ id: row.id, groupId: pickFor(row), confirm: true })),
    );
    // A failure shows under the table; the rows that saved stay saved.
    const done = results.filter((result) => result.status === "fulfilled").length;
    if (done > 0) showToast(`${done} placed`);
  }

  const columns = placementColumns({ label, groupName, groups, place, choose });

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-3 gap-3 sm:max-w-[560px]">
        <StatTile label="Placed" value={placed} tone={placed > 0 ? "good" : "neutral"} />
        <StatTile label="Not sure yet" value={unsure} />
        <StatTile label="To place" value={remaining.length} />
      </div>

      <DataTable
        caption={`${label("participant", { plural: true, capital: true })} and their ${label("group", { plural: true })}`}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={participants.isLoading}
        defaultSort={{ id: "status", direction: "desc" }}
        empty={{
          title: `No ${label("participant", { plural: true })} yet`,
          message: `They show here when ${label("member", { plural: true })} add them.`,
        }}
        countLabel={(count) =>
          `${count} ${count === 1 ? label("participant") : label("participant", { plural: true })}`
        }
      />
      {place.error ? <InlineError error={place.error} /> : null}

      <SaveBar
        note={
          remaining.length === 0
            ? "Nobody is left to place. Each change sends a note to the family."
            : `Confirm all takes the ${label("member")}'s pick for ${confirmable.length} ${confirmable.length === 1 ? "row" : "rows"}. Each change sends a note to the family.`
        }
      >
        <Button
          disabled={confirmable.length === 0}
          loading={place.isPending && confirmable.length > 0}
          onClick={() => void confirmAll()}
        >
          Confirm all
        </Button>
      </SaveBar>
    </div>
  );
}
